<template>
    <div>
        <a-table
            data-cy="report-templates-list"
            :columns="getColumns"
            :dataSource="data"
            :rowKey="(record) => record.id"
            :pagination="pagination"
            :locale="locale"
            :loading="loading"
            :rowSelection="showDeleteButton ? { selectedRowKeys: selected_row_keys, onChange: onSelectChange } : null"
            @change="handleTableChange"
            size="middle"
            class="ant-table-striped"
        >
            <template #bodyCell="{ column, record, text }">
                <template v-if="column.key === 'actions'">
                    <a-button-group>
                        <a-tooltip placement="top" :title="$t('report.table.buttons.edit')" v-if="showEditButton">
                            <a-button
                                class="text-blue-400 hover:text-blue-400 focus:text-blue-400"
                                data-cy="btn-report-edit-single"
                                @click="onTableAction(record.id, 'edit', record.name)"
                                :disabled="recordsSelected"
                            >
                                <EditOutlined />
                            </a-button>
                        </a-tooltip>
                        <a-tooltip
                            placement="top"
                            :title="$t('report.table.buttons.print')"
                            v-if="record.is_active == 1"
                        >
                            <a-button
                                class="text-default-gray hover:text-default-gray focus:text-default-gray"
                                data-cy="btn-report-generate-single"
                                @click="onTableAction(record.id, 'generate', record.name)"
                                :disabled="recordsSelected"
                            >
                                <PrinterOutlined />
                            </a-button>
                        </a-tooltip>
                        <a-tooltip placement="top" :title="$t('report.table.buttons.delete')" v-if="showDeleteButton">
                            <a-button
                                class="text-default-red hover:text-default-red focus:text-default-red"
                                data-cy="btn-report-delete-single"
                                @click="onTableAction(record.id, 'delete', record.name)"
                                :disabled="recordsSelected"
                            >
                                <DeleteOutlined />
                            </a-button>
                        </a-tooltip>
                    </a-button-group>
                </template>
                <template v-else-if="column.key === 'is_active'">
                    <CheckOutlined v-if="record.is_active" />
                    <CloseOutlined v-if="!record.is_active" />
                </template>
                <template v-else>
                    {{ text }}
                </template>
            </template>
        </a-table>
        <span v-if="data.length > 0" data-cy="page-item-range" class="page-item-range">
            {{
                $t('report.table.page_range', {
                    pi_start: pageItemStart,
                    pi_end: pageItemEnd,
                    total: pagination.total,
                })
            }}
        </span>
    </div>
</template>
<script>
import User from '@/models/user.js';
import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined, PrinterOutlined } from '@ant-design/icons-vue';

export default {
    components: { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined, PrinterOutlined },
    data() {
        return {
            data: [],
            pagination: {
                pageSize: 10,
                showSizeChanger: false,
                total: 0,
            },
            locale: {
                emptyText: this.$t('report.table.no_data'),
            },
            loading: false,
            processing: false,
            current_filter: {},
            sorter: {},
            params: {},
        };
    },
    props: {
        selected_row_keys: Array,
    },
    mounted() {
        this.fetch();
    },
    computed: {
        getColumns() {
            return [
                {
                    key: 'actions',
                    width: 100,
                },
                {
                    title: this.$t('report.table.headers.report_name'),
                    key: 'report_name',
                    dataIndex: 'report_name',
                    sorter: true,
                    width: 190,
                },
                {
                    title: this.$t('report.table.headers.report_type'),
                    key: 'report_type',
                    dataIndex: 'report_type',
                    sorter: true,
                },
                {
                    title: this.$t('report.table.headers.description'),
                    key: 'description',
                    dataIndex: 'description',
                    sorter: true,
                },
                {
                    title: this.$t('report.table.headers.is_active'),
                    key: 'is_active',
                    dataIndex: 'is_active',
                    sorter: true,
                },
            ];
        },
        pageItemStart() {
            let current_page = this.pagination.current;

            if (current_page === undefined) current_page = 1;

            return (current_page - 1) * this.pagination.pageSize + 1;
        },
        pageItemEnd() {
            let current_page = this.pagination.current,
                page_item_end = 0;

            if (current_page === undefined) current_page = 1;

            page_item_end = current_page * this.pagination.pageSize;

            if (this.pagination.total !== undefined && this.pagination.total < page_item_end) {
                return this.pagination.total;
            } else {
                return page_item_end;
            }
        },
        showEditButton() {
            return User.can('manage-report-template');
        },
        showDeleteButton() {
            return User.can('manage-report-template');
        },
        recordsSelected() {
            return this.selected_row_keys.length > 0;
        },
    },
    methods: {
        handleTableChange(pagination, filters, sorter) {
            const pager = { ...this.pagination };

            pager.current = pagination.current;

            this.pagination = pager;
            this.current_filter = filters;
            this.sorter = sorter;

            this.fetch({
                ...this.params,
                results: pagination.pageSize,
                page: pagination.current,
                sortField: sorter.field,
                sortOrder: sorter.order,
                ...filters,
            });
        },
        fetch(params = {}) {
            this.loading = true;
            this.params = params;

            this.$http
                .get('/api/reports', {
                    params: {
                        ...params,
                    },
                })
                .then((response) => {
                    const pagination = { ...this.pagination };

                    // Read total count from server
                    pagination.total = response.data.meta.total_count;

                    this.loading = false;
                    this.data = response.data.data;
                    this.pagination = pagination;
                    this.$emit('onSelectChange', []);
                })
                .catch((error) => {
                    if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVRTF01' }),
                        });
                    }
                });
        },
        reload() {
            const filters = this.current_filter;

            this.fetch({
                ...this.params,
                results: this.pagination.pageSize,
                page: this.pagination.current,
                sortField: this.sorter.field,
                sortOrder: this.sorter.order,
                ...filters,
            });
        },
        onSelectChange(selected_row_keys, selected_rows) {
            this.$emit('onSelectChange', selected_row_keys);
        },
        onTableAction(id, key, name) {
            this.$emit('onTableAction', {
                id: id,
                key: key,
                name: name,
            });
        },
    },
};
</script>
